import { Button, CircularProgress, Dialog, Stack, Typography } from '@mui/material';
import { Text } from '@eo-locale/react';
import { styled } from '@guest-widgets/core';

import { LoadingContainer } from '../StyledSharedComponents';
import { useCart } from '../contexts/cartContext/cartContext';
import { ErrorMessage } from '../Error';

export type WarningProps = {
  onClose: () => void;
  onConfirm: () => void;
  open: boolean;
  productName: string;
  rateLimitError?: boolean;
};

export const WarningDialog = ({
  productName,
  open,
  onClose,
  onConfirm,
  rateLimitError,
}: WarningProps) => {
  const { removeItem } = useCart();

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      aria-labelledby="warning-dialog"
      disableEnforceFocus
    >
      <CustomDialogContent spacing={4}>
        <WarningText variant="h3">
          <Text id="are-you-sure-want-go-back" />
        </WarningText>
        <DescriptionText variant="body1">
          <Text
            id="any-selections-made-will-be-lost"
            html
            productName={`<strong >${productName}</strong>`}
          />
        </DescriptionText>
        <ButtonsWrapper>
          <Button variant="outlined" color="primary" onClick={onClose}>
            <Text id="stay-on-this-page" />
          </Button>
          <Button variant="contained" color="primary" onClick={onConfirm}>
            <Text id="yes-go-back" />
          </Button>
        </ButtonsWrapper>
        {rateLimitError && <ErrorMessage show messageKey={'rate-limited-error'} mt={0} mb={0} />}
      </CustomDialogContent>
      {removeItem.isLoading && (
        <LoadingContainer>
          <CircularProgress size={50} />
        </LoadingContainer>
      )}
    </StyledDialog>
  );
};

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: theme.spacing(4),
  },
}));

const CustomDialogContent = styled(Stack)(({ theme: { spacing } }) => ({
  display: 'flex',
  maxWidth: 440,
  flexDirection: 'column',
  padding: spacing(5, 6),
}));

const ButtonsWrapper = styled(Stack)(({ theme: { spacing, palette } }) => ({
  display: 'flex',
  gap: spacing(4),
  justifyContent: 'flex-end',
  flexDirection: 'row',
  marginBlock: spacing(2),
  '& > button[variant="contained"]': {
    color: palette.getContrastText(palette.primary.main),
  },
  '@media (max-width: 420px)': {
    flexDirection: 'column-reverse',
  },
}));

const WarningText = styled(Typography)(({ theme: { spacing } }) => ({
  color: 'inherit',
  lineHeight: spacing(6),
  textAlign: 'left',
}));

const DescriptionText = styled(Typography)({
  color: 'inherit',
  fontWeight: 400,
  lineHeight: '22px',
  textAlign: 'left',
});
